import { useState, type ReactNode } from 'react';
import { DateTime, Num, Plural, T, Var } from 'gt-react';
import { formatCurrency, parseDateOnlyLocal } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  sortVendorCandidates,
  vendorCartStatus,
  vendorExtendedCost,
  type VendorCandidate,
  type VendorCandidateSort,
} from '../utils/vendorCandidateRows';
import { formatUnitCost } from './unitCostFormat';

/** "Mar 2026": when the vendor last sold the SKU. */
const LAST_ORDERED_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  year: 'numeric',
};

function LeadTime({ vendor }: { vendor: VendorCandidate }) {
  const { days, source } = vendor.leadTime;
  if (days == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span>
      {source === 'measured_vendor' ? (
        <T context="supplier picker lead time: measured from this vendor's own receipts, in days">
          <Num>{days}</Num>d measured
        </T>
      ) : (
        <T context="supplier picker lead time: from the vendor catalog, in days">
          <Num>{days}</Num>d catalog
        </T>
      )}
    </span>
  );
}

function CartStatus({
  vendor,
  cart,
}: {
  vendor: VendorCandidate;
  cart: { cartTotal: number; movThreshold: number | null } | undefined;
}) {
  const status = vendorCartStatus(
    cart,
    vendor.movThreshold,
    vendorExtendedCost(vendor),
  );
  if (status.cart === 'unknown') {
    return (
      <span className="text-muted-foreground">
        {status.movThreshold == null ? (
          <T context="supplier picker: the vendor has no minimum order value">
            No MOV
          </T>
        ) : (
          <T context="supplier picker: the vendor's minimum order value; its cart total isn't loaded on the board">
            MOV <Var>{formatCurrency(status.movThreshold)}</Var>
          </T>
        )}
      </span>
    );
  }
  if (status.shortOfMov == null) {
    return (
      <T context="supplier picker: the vendor has no minimum order value">
        No MOV
      </T>
    );
  }
  return status.shortOfMov > 0 ? (
    <span className="text-amber-700">
      <T context="supplier picker: how much the vendor's cart still needs to reach its minimum order value with this line in it">
        <Var>{formatCurrency(status.shortOfMov)}</Var> to MOV
      </T>
    </span>
  ) : (
    <span className="text-emerald-700">
      <T context="supplier picker: the vendor's cart meets its minimum order value with this line in it">
        MOV met
      </T>
    </span>
  );
}

function VendorNotes({ vendor }: { vendor: VendorCandidate }) {
  const lastOrdered =
    vendor.history == null
      ? null
      : parseDateOnlyLocal(vendor.history.lastOrderedAt);
  return (
    <span className="col-span-full flex flex-wrap gap-x-2 text-[10px] text-muted-foreground">
      {vendor.history != null && lastOrdered != null && (
        <span>
          <T context="supplier picker: this SKU was bought from the vendor before; how many orders and when the last one was">
            Bought before: <Num>{vendor.history.orderCount}</Num>{' '}
            <Plural n={vendor.history.orderCount} one="order" other="orders" />,
            last{' '}
            <DateTime options={LAST_ORDERED_FORMAT}>{lastOrdered}</DateTime>
          </T>
        </span>
      )}
      {vendor.pivotsThroughWarehouseId != null && (
        <span>
          <T context="supplier picker: the vendor doesn't ship to this location; the buy goes to a hub and transfers on">
            Ships to a hub, then transfers here
          </T>
        </span>
      )}
      {!vendor.priced && (
        <span>
          <T context="supplier picker: the vendor has no price for this SKU on file, so it can't be chosen">
            No price on file
          </T>
        </span>
      )}
    </span>
  );
}

function SortButton({
  value,
  sort,
  onSort,
  children,
}: {
  value: VendorCandidateSort;
  sort: VendorCandidateSort;
  onSort: (sort: VendorCandidateSort) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={sort === value}
      className={cn(
        'rounded px-1.5 py-0.5',
        sort === value
          ? 'bg-muted font-medium text-foreground'
          : 'hover:bg-muted/60',
      )}
      onClick={() => onSort(value)}
    >
      {children}
    </button>
  );
}

const SORT_BY_COST: VendorCandidateSort = 'cost';
const SORT_BY_LEAD_TIME: VendorCandidateSort = 'lead_time';

function SortToggle({
  sort,
  onSort,
}: {
  sort: VendorCandidateSort;
  onSort: (sort: VendorCandidateSort) => void;
}) {
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
      <SortButton value={SORT_BY_COST} sort={sort} onSort={onSort}>
        <T context="supplier picker sort: by unit cost">Cost</T>
      </SortButton>
      <SortButton value={SORT_BY_LEAD_TIME} sort={sort} onSort={onSort}>
        <T context="supplier picker sort: by lead time">Lead time</T>
      </SortButton>
    </span>
  );
}

type CartTotals = { cartTotal: number; movThreshold: number | null };

// fallow-ignore-next-line complexity
function VendorCandidateRow({
  vendor,
  isCurrent,
  disabled,
  cart,
  onSelect,
}: {
  vendor: VendorCandidate;
  isCurrent: boolean;
  disabled: boolean;
  cart: CartTotals | undefined;
  onSelect: (vendor: VendorCandidate) => void;
}) {
  const ext = vendorExtendedCost(vendor);
  return (
    <DropdownMenuItem
      disabled={disabled || isCurrent || !vendor.priced}
      onSelect={() => onSelect(vendor)}
      className="grid grid-cols-[minmax(0,1fr)_6rem_5.5rem_8rem] items-start gap-x-3 gap-y-0.5 text-xs data-[disabled]:opacity-100"
    >
      <span
        className={cn(
          'truncate',
          isCurrent && 'font-semibold',
          !vendor.priced && 'text-muted-foreground',
        )}
      >
        {vendor.supplierName}
        {isCurrent && (
          <span className="ml-1 font-normal text-muted-foreground">
            <T context="supplier picker: marks the line's current vendor">
              (current)
            </T>
          </span>
        )}
      </span>
      <span className="flex flex-col items-end tabular-nums leading-tight">
        <span className="font-medium">
          {vendor.unitPrice == null ? '—' : formatUnitCost(vendor.unitPrice)}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {ext == null ? '' : formatCurrency(ext)}
        </span>
      </span>
      <span className="whitespace-nowrap text-right tabular-nums">
        <LeadTime vendor={vendor} />
      </span>
      <span className="whitespace-nowrap text-right">
        {vendor.priced && !isCurrent && (
          <CartStatus vendor={vendor} cart={cart} />
        )}
      </span>
      <VendorNotes vendor={vendor} />
    </DropdownMenuItem>
  );
}

/**
 * Other vendors for the line's SKU in the unit cost dropdown (GEN-3246): each
 * quoted at the line's quantity, with lead time, the cart it would join and
 * whether it was bought from before. Choosing one moves the line to that
 * vendor's cart.
 */
export function VendorCandidatesSection({
  vendors,
  isLoading,
  isError,
  onRetry,
  currentSupplierId,
  quantity,
  disabled,
  cartFor,
  onSelect,
}: {
  vendors: readonly VendorCandidate[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  currentSupplierId: string | null | undefined;
  quantity: number;
  disabled: boolean;
  cartFor: (
    vendor: VendorCandidate,
  ) => { cartTotal: number; movThreshold: number | null } | undefined;
  onSelect: (vendor: VendorCandidate) => void;
}) {
  const [sort, setSort] = useState<VendorCandidateSort>('cost');
  const rows = sortVendorCandidates(vendors, currentSupplierId, sort);

  return (
    <div className="space-y-1 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold uppercase tracking-wide text-muted-foreground">
          <T context="supplier picker heading in the unit cost dropdown, with the line's quantity">
            Suppliers · qty <Num>{quantity}</Num>
          </T>
        </span>
        <SortToggle sort={sort} onSort={setSort} />
      </div>
      {isError ? (
        <div className="flex items-center gap-2 py-2 text-muted-foreground">
          <T context="supplier picker: the vendor list failed to load">
            Could not load suppliers.
          </T>
          <button
            type="button"
            className="rounded px-1.5 py-0.5 font-medium text-foreground hover:bg-muted"
            onClick={onRetry}
          >
            <T context="supplier picker: retry loading the vendor list">
              Retry
            </T>
          </button>
        </div>
      ) : isLoading ? (
        <div className="py-2 text-muted-foreground">
          <T context="supplier picker: vendors are loading">
            Loading suppliers…
          </T>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-2 text-muted-foreground">
          <T context="supplier picker: no vendor sells this SKU">
            No other supplier on file.
          </T>
        </div>
      ) : (
        <div role="group" className="space-y-0.5">
          {rows.map((vendor) => (
            <VendorCandidateRow
              key={vendor.supplierId}
              vendor={vendor}
              isCurrent={vendor.supplierId === currentSupplierId}
              disabled={disabled}
              cart={cartFor(vendor)}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground">
        <T context="supplier picker footer: what choosing another vendor does">
          Choosing a supplier moves the line to its cart.
        </T>
      </div>
    </div>
  );
}
